import { useMemo } from 'react';
import { hrSidebarSections } from '@/constant/hr-sidebar-menu';
import { settingsSections as allSettingsSections } from '@/constant/settings-menu';
import { useAuthStore } from '@/state/store/auth';
import { SettingsSection } from '@/types/sidebar.type';
import { filterMenuItems } from './use-filtered-menu';

type Access = Parameters<typeof filterMenuItems>[1];

/**
 * The settings groups the user can open. A built page shows to whoever may open it; the Soon pages show
 * to everyone who can open at least one built page, and to nobody else. A group left empty is dropped.
 */
export const permittedSettingsSections = (access: Access): SettingsSection[] => {
  const built = allSettingsSections.map((section) => ({
    ...section,
    items: filterMenuItems(
      section.items.filter((item) => !item.soon),
      access
    ),
  }));
  const canOpenSettings = built.some((section) => section.items.length > 0);
  if (!canOpenSettings) return [];

  return allSettingsSections
    .map((section) => {
      const allowed = new Set(built.find((b) => b.title === section.title)?.items.map((i) => i.id));
      return {
        ...section,
        items: section.items.filter((item) => item.soon || allowed.has(item.id)),
      };
    })
    .filter((section) => section.items.length > 0);
};

/**
 * The sidebar sections the signed-in user can see: items they lack permission for are dropped,
 * and a section left with no items is dropped entirely. `settingsSections` are the groups of the
 * Settings area (empty: no Settings gear).
 */
export const usePermittedSidebar = () => {
  const user = useAuthStore((state) => state.user);
  const organizationId = useAuthStore((state) => state.selectedOrgId);

  return useMemo(() => {
    const access = { user, organizationId };
    const sections = hrSidebarSections
      .map((section) => ({
        ...section,
        items: filterMenuItems(section.items, access),
      }))
      .filter((section) => section.items.length > 0);
    const settingsSections = permittedSettingsSections(access);

    // First page the user can open — the main menu in order, then Settings — where a blocked URL sends
    // them. A Soon page is never a landing page.
    const firstSettingsPage = settingsSections
      .flatMap((section) => section.items)
      .find((item) => !item.soon);
    const landingPath = sections[0]?.items[0]?.path ?? firstSettingsPage?.path ?? null;

    return { sections, settingsSections, landingPath };
  }, [user, organizationId]);
};
